
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';
import { PROVIDER_TYPES } from '@/types/listings';

const SignUpForm = () => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    roleCategory: '',
    role: '',
    providerType: '',
    organization: '',
    npi: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate required fields
    if (!formData.roleCategory) {
      setError('Please select a role category');
      setLoading(false);
      return;
    }

    if (!formData.role) {
      setError('Please select a specific role');
      setLoading(false);
      return;
    }

    try {
      await signUp(formData.email, formData.password, {
        name: formData.name,
        role: formData.role as any,
        organization: formData.organization,
        npi: formData.npi,
      });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Create Account</CardTitle>
        <CardDescription>
          Join CareKonnect to streamline care placements
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="roleCategory">Role Category</Label>
            <Select 
              value={formData.roleCategory} 
              onValueChange={(value) => setFormData({ 
                ...formData, 
                roleCategory: value, 
                role: value === 'provider' ? '' : '',
                providerType: ''
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your role category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="provider">Provider</SelectItem>
                <SelectItem value="placement_professional">Placement Professional</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.roleCategory === 'provider' && (
            <div className="space-y-2">
              <Label htmlFor="providerType">Provider Type</Label>
              <Select 
                value={formData.providerType} 
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  providerType: value,
                  role: value === 'hss_agency' ? 'hss_provider' : 'provider'
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select provider type" />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {formData.roleCategory === 'placement_professional' && (
            <div className="space-y-2">
              <Label htmlFor="role">Specific Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your specific role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="case_manager">Case Manager</SelectItem>
                  <SelectItem value="social_worker">Social Worker</SelectItem>
                  <SelectItem value="discharge_planner">Hospital Discharge Planner</SelectItem>
                  <SelectItem value="housing_stabilization_worker">Housing Stabilization Worker</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="organization">Organization</Label>
            <Input
              id="organization"
              value={formData.organization}
              onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
              required
            />
          </div>
          
          {(formData.role === 'provider' || formData.role === 'hss_provider') && (
            <div className="space-y-2">
              <Label htmlFor="npi">
                {formData.role === 'hss_provider' ? 'NPI or UMPI Number' : 'NPI Number'}
              </Label>
              <Input
                id="npi"
                value={formData.npi}
                onChange={(e) => setFormData({ ...formData, npi: e.target.value })}
                placeholder={formData.role === 'hss_provider' ? 'National Provider Identifier or UMPI' : 'National Provider Identifier'}
              />
            </div>
          )}
          
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default SignUpForm;
